import React from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface QuestionPickRowProps {
  questionNumber: number;
  questionText: string | null;
  optionText: string | null;
  optionImage: string | null;
  isCorrect: boolean | null;
  pointsEarned: number;
  isCurrentUser?: boolean;
}

export const QuestionPickRow: React.FC<QuestionPickRowProps> = ({
  questionNumber,
  questionText,
  optionText,
  optionImage,
  isCorrect,
  pointsEarned,
  isCurrentUser = false,
}) => {
  const { t } = useTranslation();
  // Current user expands under the white "my score" row → dark text;
  // other users expand on dark surface → light text.
  const baseTextColor = isCurrentUser ? 'text-background' : 'text-white';
  const secondaryTextColor = isCurrentUser ? 'text-background/70' : 'text-muted-foreground';

  return (
    <div className="flex items-center gap-3 py-2 font-sans">
      {/* Question Number */}
      <span className={`w-4 text-sm font-medium shrink-0 ${secondaryTextColor}`}>
        {questionNumber}
      </span>

      {/* Image */}
      {optionImage && (
        <img
          src={optionImage}
          alt=""
          className="w-8 h-8 object-contain rounded shrink-0"
          onError={e => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}

      {/* Question Type + Answer (stacked) */}
      <div className="flex flex-col flex-1 min-w-0">
        {questionText && (
          <span className={`text-xs truncate ${secondaryTextColor}`}>{questionText}</span>
        )}
        <span className={`truncate text-sm ${baseTextColor}`}>
          {optionText || t('leaderboard.noSelection')}
        </span>
      </div>

      {/* Points + Correct/Incorrect */}
      <div className="flex items-center gap-2 shrink-0">
        {isCorrect !== null && (
          <div
            className={`w-5 h-5 rounded-full flex items-center justify-center ${
              isCorrect ? 'bg-success/20' : 'bg-primary/20'
            }`}
          >
            {isCorrect ? (
              <Check className="w-3 h-3 text-success" />
            ) : (
              <X className="w-3 h-3 text-primary" />
            )}
          </div>
        )}
        <span className={`text-sm font-medium ${secondaryTextColor}`}>
          {pointsEarned > 0 ? `+${pointsEarned}` : pointsEarned}
        </span>
      </div>
    </div>
  );
};
